import './balancer-transactions.scss';
import { Enum, PaginationList } from '../../../../core/domain';
import { useEffect, useState } from 'react';
import { getIconPath } from '../../../../core/utils/getIconPath';
import { message, Tag } from 'antd';
import { digitSperator } from '../../../../core/utils/decimalExtentions';
import moment from 'jalali-moment';
import { DeleteOutlined, EditOutlined, FilterOutlined, PlusOutlined } from '@ant-design/icons';
import { ReactComponent as CalendarIcon } from 'assets/icons/dash-line-icons/calendar.svg';
import { getOrderSides } from '../../../../core/repositories/orders';
import { Card } from '../../../../components/card/card';
import { Input } from '../../../../components/input/input';
import { Button, buttonType } from '../../../../components/button/button';
import { Table } from '../../../../components/table/table';
import { Dropdown as CustomDropdown } from 'components/dropdown/dropdown';
import DatePicker from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import { Page } from 'components/page/page';
import { BalancerTransaction } from 'core/domain/balancer-transaction/balancer-transaction';
import { useNavigate } from 'react-router-dom';
import { getAllBalancerTransactions, removeBalancerTransaction } from 'core/repositories/balancer-transaction';
import { jFormat } from 'core/utils/jFormat';
import { AddBalancerTransactionModal } from './add/add';

const initialFilters = {
    side: {
        id: -1,
        title: 'همه انواع معامله',
    },
};

export const BalancerTransactions = ({ openAddOnLoad = false }: { openAddOnLoad?: boolean }) => {
    const [addModalIsOpen, setAddModalIsOpen] = useState(openAddOnLoad);
    const [loading, setLoading] = useState<boolean>(false);
    const [transactions, setTransactions] = useState<PaginationList<BalancerTransaction>>();
    const [selectedTransaction, setSelectedTransaction] = useState<BalancerTransaction>();

    const [deleteModalIsOpen, setDeleteModalIsOpen] = useState<boolean>(false);
    const [deleteModalIsLoading, setDeleteModalIsLoading] = useState<boolean>(false);

    //Enums
    const [orderSides, setOrderSides] = useState<Enum[]>([initialFilters.side]);

    const [pageSize, setPageSize] = useState<number>(10);

    const [query, setQuery] = useState<string>('');

    const [filterValues, setFilterValues] = useState<{
        startDate?: string;
        endDate?: string;
        side: Enum;
    }>({
        side: initialFilters.side,
    });

    const navigate = useNavigate();

    const closeAddModal = () => {
        setAddModalIsOpen(false);
        if (openAddOnLoad) navigate('..', { replace: true });
    };

    const columns: any[] = [
        {
            name: 'نماد',
            id: 'symbol',
            selector: (row: BalancerTransaction) => row.symbol.toUpperCase(),
            width: '15%',
        },
        {
            name: 'قیمت خرید/فروش',
            id: 'onPrice',
            selector: (row: BalancerTransaction) => row.onPrice,
            cell: (row: BalancerTransaction) => digitSperator(row.onPrice),
            width: '15%',
        },
        {
            name: 'مقدار',
            id: 'amount',
            selector: (row: BalancerTransaction) => row.amount,
            cell: (row: BalancerTransaction) => digitSperator(row.amount),
            width: '15%',
        },
        {
            name: 'نوع معامله',
            id: 'side',
            selector: (row: BalancerTransaction) => row.side,
            cell: (row: BalancerTransaction) => (
                <Tag color={`${row.side.id === 1 ? 'green' : 'red'}`}>{row.side.id === 1 ? 'خرید' : 'فروش'}</Tag>
            ),
            width: '15%',
        },
        {
            name: 'تاریخ ایجاد',
            id: 'createdOn',
            selector: (row: BalancerTransaction) => row.createdOn,
            cell: (row: BalancerTransaction) => (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <span className="day">{moment(row.createdOn).locale('fa').local().format('jYYYY/MM/DD')}</span>
                    <span className="time">{moment(row.createdOn).local().format('HH:mm')}</span>
                </div>
            ),
            width: '20%',
        },
        {
            name: 'عملیات',
            id: 'actions',
            cell: (row: BalancerTransaction) => (
                <div className="actions">
                    <EditOutlined onClick={() => navigate(`${row.id}`)} />

                    <DeleteOutlined
                        onClick={() => {
                            setSelectedTransaction(row);
                            setDeleteModalIsOpen(true);
                        }}
                    />
                </div>
            ),
        },
    ];

    const handleDelete = async () => {
        if (!selectedTransaction) return;
        try {
            removeBalancerTransaction(setDeleteModalIsLoading, selectedTransaction.id, () => {
                setDeleteModalIsOpen(false);
                fetchData();
            });
        } catch {
            message.error('خطا در حذف تراکنش');
        }
    };

    const fetchData = () => {
        getAllBalancerTransactions(setLoading, setTransactions, 1, pageSize, query);
    };

    const filterHandle = () => {
        const params = new URLSearchParams();
        if (filterValues.side.id !== -1) params.set('side', String(filterValues.side.id));
        if (filterValues.startDate) params.set('startDate', jFormat(filterValues.startDate));
        if (filterValues.endDate) params.set('endDate', jFormat(filterValues.endDate));
        const queryString = params.toString();
        const nextQuery = queryString ? `?${queryString}` : '';
        setQuery(nextQuery);
        getAllBalancerTransactions(setLoading, setTransactions, 1, pageSize, nextQuery);
    };

    useEffect(() => {
        getOrderSides(() => {}, setOrderSides);
        fetchData();
    }, []);

    return (
        <Page className="pmx-balancer-transactions" title="مدیریت تامین موجودی">
            <Card blur={loading}>
                <div className="balancer-transactions-header">
                    <div>
                        <h2>تراکنش‌های تأمین موجودی</h2>
                        <p>سوابق خرید و فروش ارز</p>
                    </div>
                    <Button
                        type={buttonType.info}
                        icon={<PlusOutlined />}
                        text="افزودن تراکنش"
                        onClick={() => setAddModalIsOpen(true)}
                    />
                </div>
                <div className="filter-row">
                    <div className="fields">
                        <CustomDropdown
                            items={[initialFilters.side, ...orderSides.filter((item) => item.id !== -1)]}
                            onChangeHandle={(value) =>
                                setFilterValues({
                                    ...filterValues,
                                    side:
                                        orderSides.find((item) => item.id === value && item.id !== -1) ??
                                        initialFilters.side,
                                })
                            }
                            selectedOption={filterValues.side}
                            defaultSelected={filterValues.side}
                        />

                        <DatePicker
                            className="date-picker"
                            value={filterValues.startDate}
                            calendar={persian}
                            locale={persian_fa}
                            render={
                                <div>
                                    <Input
                                        value={filterValues.startDate ?? ''}
                                        readOnly
                                        icon={<CalendarIcon />}
                                        placeholder="از تاریخ"
                                    />
                                </div>
                            }
                            onChange={(e) => {
                                setFilterValues({
                                    ...filterValues,
                                    startDate: e?.format(),
                                });
                            }}
                        />
                        <DatePicker
                            className="date-picker"
                            value={filterValues.endDate}
                            calendar={persian}
                            locale={persian_fa}
                            render={
                                <div>
                                    <Input
                                        value={filterValues.endDate ?? ''}
                                        readOnly
                                        icon={<CalendarIcon />}
                                        placeholder="تا تاریخ"
                                    />
                                </div>
                            }
                            onChange={(e) => {
                                setFilterValues({
                                    ...filterValues,
                                    endDate: e?.format(),
                                });
                            }}
                        />
                    </div>

                    <div className="buttons">
                        <Button
                            loading={loading}
                            disabled={loading}
                            onClick={filterHandle}
                            icon={<FilterOutlined />}
                            text="اعمال فیلتر"
                            type={buttonType.secondary}
                        />
                        <Button
                            type={buttonType.white}
                            text="پاک کردن فیلتر"
                            onClick={() => {
                                setFilterValues({
                                    startDate: '',
                                    endDate: '',
                                    side: initialFilters.side,
                                });
                                setQuery('');
                                getAllBalancerTransactions(setLoading, setTransactions, 1, pageSize, '');
                            }}
                        />
                    </div>
                </div>
                {transactions && (
                    <Table
                        filters={query}
                        setloader={setLoading}
                        setdata={setTransactions}
                        getdata={getAllBalancerTransactions}
                        pagination={transactions.pagination}
                        data={transactions.items}
                        column={columns}
                        setPageSize={setPageSize}
                        pageSize={transactions?.pagination?.pageSize}
                        className={'balancer-transactions-table'}
                    />
                )}
            </Card>
            <AddBalancerTransactionModal
                isOpen={addModalIsOpen}
                orderSides={orderSides}
                onClose={closeAddModal}
                onSuccess={() => {
                    closeAddModal();
                    if (!openAddOnLoad) fetchData();
                }}
            />
        </Page>
    );
};
